
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { RadioTower, TrendingUp, Music, Radio } from 'lucide-react';
import { useMockRadioData } from '@/hooks/useMockRadioData';

const RadioSpinsOverview: React.FC = () => {
  const { recentSpins, totalSpins, spinsByTrack, weeklySpinsData } = useMockRadioData();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Spins
            </CardTitle>
            <RadioTower className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalSpins.total.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              +{totalSpins.increase} from last month
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Stations
            </CardTitle>
            <Radio className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalSpins.stations}</div>
            <p className="text-xs text-muted-foreground">
              Across {totalSpins.countries} countries
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Top Market
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalSpins.topMarket}</div>
            <p className="text-xs text-muted-foreground">
              {totalSpins.topMarketSpins} spins this month
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Top Track
            </CardTitle>
            <Music className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold truncate">{totalSpins.topTrack}</div>
            <p className="text-xs text-muted-foreground">
              {totalSpins.topTrackSpins} spins this month
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Weekly Spins</CardTitle>
            <CardDescription>Radio plays across all stations for the past 4 weeks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weeklySpinsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="spins" fill="#8884d8" name="Spins" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Top Tracks</CardTitle>
            <CardDescription>Tracks with the most radio spins</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Track</TableHead>
                  <TableHead className="text-right">Spins</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {spinsByTrack.map((track, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{track.title}</TableCell>
                    <TableCell className="text-right">{track.spins}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Radio Spins</CardTitle>
          <CardDescription>Your latest tracks played on radio stations</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Track</TableHead>
                <TableHead>Station</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Date & Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentSpins.map((spin, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{spin.track}</TableCell>
                  <TableCell>{spin.station}</TableCell>
                  <TableCell>{spin.location}</TableCell>
                  <TableCell>{spin.datetime}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default RadioSpinsOverview;
